import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
import type {
    AppSharedDataRawData,
    BEShowUserLoginPagePropKeys,
} from '@/Shared/AutoGenerated/Main'
import { useApiForm } from '@/React/Shared/Component/Forms/ApiForm'
import { ApiFormControl } from '@/React/Shared/Component/Forms/ApiFormControl'
import { TextInput } from '@/React/Shared/Component/Forms/TextInput'
import { ApiFormButton } from '@/React/Shared/Component/Forms/ApiFormButton'
import { FlashApiForm } from '@/React/Shared/Component/Flash/FlashApiForm'
import { AuthLayout } from '@/React/Shared/Layout/App/Auth/AuthLayout'
import type { EnforceBEPagePropKeys } from '@/Shared/Helper/EnforceBEPagePropKeys'
import AppSharedData from '@/Shared/Entity/AppSharedData'
import { UrlFactory } from '@/Shared/Url/UrlFactory'

type UserLoginPageProps = EnforceBEPagePropKeys<
    {
        appSharedDataData: AppSharedDataRawData
        googleSocialLoginEnabled: boolean
        microsoftSocialLoginEnabled: boolean
        destinationUrl: string
    },
    BEShowUserLoginPagePropKeys
>

export default function UserLoginPage({
    appSharedDataData,
    googleSocialLoginEnabled,
    microsoftSocialLoginEnabled,
    destinationUrl,
}: UserLoginPageProps) {
    const appSharedData = AppSharedData.hydrate(appSharedDataData)
    const apiForm = useApiForm()
    const urlFactory = new UrlFactory()

    return (
        <AuthLayout
            {...appSharedData.getAuthLayoutProps()}
            title="Sign in to your account"
        >
            {googleSocialLoginEnabled ? (
                <>
                    <a
                        href={urlFactory.makeGoogleOAuthLoginRedirectUrl()}
                        className="flex w-full items-center justify-center rounded-lg2 bg-white p-3 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-stone-200 hover:bg-gray-50"
                    >
                        Sign in with Google
                    </a>

                    <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-sm font-medium leading-6">
                            <span className="bg-white px-6 text-gray-900">
                                Or
                            </span>
                        </div>
                    </div>
                </>
            ) : null}

            <FlashApiForm
                apiForm={apiForm}
                url={urlFactory.makeRelativeAuthLoginPageUrl(destinationUrl)}
            >
                <ApiFormControl
                    inputName={RequestBodyKeyEnum.Auth_User_Login_Email}
                    label="Email"
                    required
                    apiFormResponse={apiForm.response}
                >
                    <TextInput
                        name={RequestBodyKeyEnum.Auth_User_Login_Email}
                        type="email"
                        autoComplete="username"
                        required
                    />
                </ApiFormControl>

                <ApiFormControl
                    inputName={RequestBodyKeyEnum.Auth_User_Login_Password}
                    label="Password"
                    required
                    apiFormResponse={apiForm.response}
                >
                    <TextInput
                        name={RequestBodyKeyEnum.Auth_User_Login_Password}
                        type="password"
                        autoComplete="current-password"
                        required
                    />
                </ApiFormControl>

                <ApiFormButton
                    apiForm={apiForm}
                    color="primary"
                    className="mt-5 w-full"
                >
                    Sign in
                </ApiFormButton>
            </FlashApiForm>

            <div className="mt-4 flex flex-col space-y-1 text-sm leading-6">
                <a
                    href={urlFactory.makeAuthRegisterPageUrl()}
                    className="font-semibold text-primary hover:text-primary-shade"
                >
                    Don't have an account?
                </a>
                <a
                    href={urlFactory.makeAuthForgotPasswordPageUrl()}
                    className="font-semibold text-primary hover:text-primary-shade"
                >
                    Forgot your password?
                </a>
            </div>
        </AuthLayout>
    )
}
